import { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Alert, Button, Drawer, Select, Skeleton, Space, Typography } from 'antd';
import { ArrowRightOutlined, SwapOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  compareRuns,
  searchRuns,
  type RunComparison,
  type RunSummary,
} from '@/api/validationRuns';
import { StatusPill } from '@/components/StatusPill';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import { CHANGE, ORDER, TYPE_LABEL, VERDICT_ALERT, identityLabel, outcomeLabel } from './compare/compareWords';
import { TrialDots } from './compare/TrialDots';
import { useRunLinks } from '@/features/scenario-intelligence/workspace/paths';

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// CompareRunsDrawer — EVAL-06 STUDIO-EVAL-06. This run against an earlier run of the same agent, scenario by
// scenario. What got worse comes first; a scenario that was edited between the runs is shown apart and never
// scored. The server decides every change (ValidationRunComparisonRules); this drawer only words and groups them.
// SI-07: the drawer now names the comparison type and its verdict, labels each side Agent · Candidate revision · Actual
// deployment, shows every trial, and opens the full Compare page (latency and cost, the controls, saving).
// ─────────────────────────────────────────────────────────────────────────────

export const CompareRunsDrawer = observer(function CompareRunsDrawer({
  run,
  open,
  onClose,
  onOpenResult,
}: {
  run: RunSummary;
  open: boolean;
  onClose: () => void;
  onOpenResult: (resultId: number) => void;
}) {
  const { themeStore } = useStore();
  const navigate = useNavigate();
  const links = useRunLinks();
  const isDark = themeStore.mode === 'dark';
  const [others, setOthers] = useState<RunSummary[] | null>(null);
  const [baselineId, setBaselineId] = useState<number | null>(null);
  const [comparison, setComparison] = useState<RunComparison | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // A different run (the drawer is reused across runs): nothing from the previous run's comparison carries over.
  useEffect(() => {
    setBaselineId(null);
    setComparison(null);
    setOthers(null);
  }, [run.id]);

  // The runs this one can be compared with: the same agent, newest first; default to the run just before it.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setError(null);
    searchRuns({ agentId: run.aiAgentId, days: 180, pageSize: 50 })
      .then((list) => {
        if (cancelled) return;
        const candidates = list.filter((r) => r.id !== run.id);
        setOthers(candidates);
        const earlier = candidates.find((r) => r.runDate < run.runDate) ?? candidates[0];
        setBaselineId((current) => current ?? earlier?.id ?? null);
      })
      .catch((err: Error) => !cancelled && setError(err.message));
    return () => {
      cancelled = true;
    };
  }, [open, run.aiAgentId, run.id, run.runDate]);

  useEffect(() => {
    if (!open || baselineId == null) return;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    setComparison(null); // review R8: the old pair's results never stay up under the new selection
    compareRuns(baselineId, run.id, controller.signal)
      .then((c) => !controller.signal.aborted && setComparison(c))
      .catch((err: Error) => !controller.signal.aborted && setError(err.message))
      .finally(() => !controller.signal.aborted && setLoading(false));
    return () => controller.abort();
  }, [open, baselineId, run.id]);

  // Only a comparison of exactly the selected pair is shown (review R8).
  const shown = comparison && comparison.baselineRunId === baselineId && comparison.candidateRunId === run.id ? comparison : null;
  const groups = ORDER.map((change) => ({ change, rows: shown?.scenarios.filter((s) => s.change === change) ?? [] }));
  const divider = isDark ? colors.slate[700] : colors.slate[200];
  const verdict = shown?.comparability;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      size="min(820px, 100vw)"
      title={
        <Space size={8}>
          <SwapOutlined />
          <span>{`Compare run #${run.id} with an earlier run`}</span>
        </Space>
      }
      extra={
        shown && (
          <Button type="primary" icon={<ArrowRightOutlined />}
            onClick={() => navigate(links.compare(`?baseline=${shown.baselineRunId}&candidate=${shown.candidateRunId}`))}>
            Open full comparison
          </Button>
        )
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <Text type="secondary" style={{ display: 'block', marginBottom: 6 }}>
            Compare against
          </Text>
          <Select
            style={{ width: '100%', maxWidth: 520 }}
            placeholder={others && others.length === 0 ? 'No other run of this agent' : 'Choose a run'}
            value={baselineId ?? undefined}
            onChange={(v) => setBaselineId(v)}
            loading={others == null && !error}
            options={(others ?? []).map((r) => ({
              value: r.id,
              label: `#${r.id} · ${dayjs(r.runDate).format('D MMM YYYY HH:mm')} · ${r.passedScenarios} of ${r.totalScenarios} passed${r.status && r.status !== 'Completed' ? ` · ${r.status}` : ''}`,
            }))}
          />
        </div>

        {error && <Alert type="error" showIcon title="Could not compare" description={error} />}
        {loading && !shown && <Skeleton active paragraph={{ rows: 4 }} />}

        {shown && (
          <>
            {verdict && (
              <Alert
                type={VERDICT_ALERT[verdict.verdict]}
                showIcon
                title={`${shown.type ? `${TYPE_LABEL[shown.type]} · ` : ''}${verdict.headline}`}
                description={verdict.detail}
              />
            )}
            {(shown.baselineIdentity || shown.candidateIdentity) && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {`Before: ${identityLabel(shown.baselineIdentity, shown.baselineRunId)} — Now: ${identityLabel(shown.candidateIdentity, shown.candidateRunId)}`}
              </Text>
            )}
            <Space size={[8, 8]} wrap>
              {groups
                .filter((g) => g.rows.length > 0)
                .map((g) => (
                  <StatusPill key={g.change} tone={CHANGE[g.change].tone}>
                    {`${CHANGE[g.change].label}: ${g.rows.length}`}
                  </StatusPill>
                ))}
            </Space>
            {shown.notes.length > 0 && (
              <Alert type="info" showIcon title="Read with care" description={shown.notes.join(' ')} />
            )}
            {groups
              .filter((g) => g.rows.length > 0)
              .map((g) => (
                <section key={g.change}>
                  <Text strong style={{ display: 'block' }}>{`${CHANGE[g.change].label} (${g.rows.length})`}</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>{CHANGE[g.change].explain}</Text>
                  <div style={{ marginTop: 6 }}>
                    {g.rows.map((row, i) => {
                      const before = outcomeLabel(row.baseline);
                      const now = outcomeLabel(row.candidate);
                      return (
                        <div
                          key={row.scenarioId}
                          style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            gap: '6px 12px',
                            padding: '8px 0',
                            borderTop: i === 0 ? undefined : `1px solid ${divider}`,
                          }}
                        >
                          <div style={{ flex: '1 1 240px', minWidth: 0 }}>
                            <Text style={{ fontFamily: "'JetBrains Mono', monospace", wordBreak: 'break-word' }}>{row.scenarioId}</Text>
                            {g.change === 'NotComparable' && (
                              <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>{row.reason}</Text>
                            )}
                          </div>
                          <Space size={4} wrap>
                            <Button size="small" disabled={!row.baseline} onClick={() => row.baseline && onOpenResult(row.baseline.resultId)}>
                              <Space size={4}>
                                <span>Before</span>
                                <StatusPill tone={before.tone}>{before.text}</StatusPill>
                              </Space>
                            </Button>
                            {(row.baseline?.trials?.length ?? 0) > 1 && <TrialDots side={row.baseline} onOpen={onOpenResult} />}
                            <Button size="small" disabled={!row.candidate} onClick={() => row.candidate && onOpenResult(row.candidate.resultId)}>
                              <Space size={4}>
                                <span>Now</span>
                                <StatusPill tone={now.tone}>{now.text}</StatusPill>
                              </Space>
                            </Button>
                            {(row.candidate?.trials?.length ?? 0) > 1 && <TrialDots side={row.candidate} onOpen={onOpenResult} />}
                          </Space>
                        </div>
                      );
                    })}
                  </div>
                </section>
              ))}
          </>
        )}
      </div>
    </Drawer>
  );
});
